// src/pages/employer/EmployerApplyFilters.jsx
// Library of standalone Apply Filters owned by the active group profile.
// Filters can be created, renamed, duplicated, edited and deleted; their
// association with positions happens later via application gateways.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { applyFilterAPI } from '../../services/api';
import { Modal, useConfirm } from '../../components/shared/UI';
import { Plus, Pencil, Trash2, Copy, Filter as FilterIcon } from 'lucide-react';

const NewFilterModal = ({ open, onClose, onCreate, saving }) => {
  const [name, setName] = useState('');
  useEffect(() => { if (open) setName(''); }, [open]);
  const canSave = name.trim().length > 0 && !saving;
  return (
    <Modal open={open} onClose={onClose} title="New apply filter" width="460px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave}
            onClick={() => onCreate(name.trim())}>
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      }>
      <label className="form-label">Filter name</label>
      <input className="form-input" autoFocus value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Right-to-work + driving licence"
        onKeyDown={(e) => { if (e.key === 'Enter' && canSave) onCreate(name.trim()); }} />
      <p style={{ fontSize: 12, color: '#777', marginTop: 8, marginBottom: 0 }}>
        Add questions to the filter on the next screen.
      </p>
    </Modal>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

export const EmployerApplyFilters = () => {
  const { activeOrg, activeProfile } = useAuth();
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();

  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    if (!activeOrg || !activeProfile) return;
    setLoading(true);
    try {
      const { data } = await applyFilterAPI.list(activeOrg.id, activeProfile.id);
      setFilters(data?.data?.filters || []);
    } catch {
      // axios interceptor handles toast
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [activeOrg?.id, activeProfile?.id]);

  const handleCreate = async (name) => {
    setCreating(true);
    try {
      const { data } = await applyFilterAPI.create(activeOrg.id, activeProfile.id, { name });
      const filter = data?.data?.filter;
      setNewOpen(false);
      toast.success('Apply filter created');
      if (filter?.id) navigate(`/employer/libraries/apply-filters/${filter.id}`);
      else reload();
    } catch {
      // interceptor
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (filter) => {
    try {
      const { data: getRes } = await applyFilterAPI.get(activeOrg.id, activeProfile.id, filter.id);
      const src = getRes?.data?.filter;
      if (!src) return;
      const payload = {
        name: `${src.name} (copy)`,
        intro: src.intro || null,
        questions: (src.questions || []).map((q, i) => ({
          order:         i,
          type:          q.type,
          prompt:        q.prompt,
          required:      !!q.required,
          config:        q.config ?? null,
          correctAnswer: q.correctAnswer ?? null,
        })),
      };
      await applyFilterAPI.create(activeOrg.id, activeProfile.id, payload);
      toast.success('Apply filter duplicated');
      reload();
    } catch {
      // interceptor
    }
  };

  const handleDelete = async (filter) => {
    if (!await confirm('Delete apply filter',
      `"${filter.name}" will be permanently removed.`)) return;
    try {
      await applyFilterAPI.delete(activeOrg.id, activeProfile.id, filter.id);
      setFilters((prev) => prev.filter((f) => f.id !== filter.id));
      toast.success('Apply filter deleted');
    } catch {
      // interceptor
    }
  };

  return (
    <div className="page-content" style={{ padding: '16px 20px 40px' }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ flex: 1 }}>Apply Filters</span>
        <button className="btn btn-primary btn-sm" onClick={() => setNewOpen(true)}>
          <Plus size={13} style={{ marginRight: 4 }} /> New filter
        </button>
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '10px 0 16px' }}>
        Reusable application questionnaires. Attach them to positions when configuring
        application gateways (coming later).
      </p>

      {loading ? (
        <p style={{ color: '#888', padding: 20 }}>Loading…</p>
      ) : filters.length === 0 ? (
        <div style={{
          background: 'white', border: '1px dashed var(--border)', borderRadius: 6,
          padding: 32, textAlign: 'center', color: '#888',
        }}>
          <FilterIcon size={28} style={{ marginBottom: 8, opacity: 0.6 }} />
          <div style={{ fontSize: 14, marginBottom: 8 }}>No apply filters yet.</div>
          <button className="btn btn-primary btn-sm" onClick={() => setNewOpen(true)}>
            <Plus size={13} style={{ marginRight: 4 }} /> Create your first filter
          </button>
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#F5F7FA' }}>
              <tr>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: '#0e4e95', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Name</th>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: '#0e4e95', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Questions</th>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: '#0e4e95', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Updated</th>
                <th style={{ padding: '8px 14px', width: 130 }}></th>
              </tr>
            </thead>
            <tbody>
              {filters.map((f) => (
                <tr key={f.id} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600, color: '#333' }}>{f.name}</td>
                  <td style={{ padding: '10px 14px', color: '#555' }}>{f.questionCount}</td>
                  <td style={{ padding: '10px 14px', color: '#777' }}>{formatDate(f.updatedAt)}</td>
                  <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                    <button className="btn btn-outline btn-sm" style={{ marginRight: 6 }}
                      onClick={() => navigate(`/employer/libraries/apply-filters/${f.id}`)}>
                      <Pencil size={12} style={{ marginRight: 4 }} /> Edit
                    </button>
                    <button className="btn btn-outline btn-sm" style={{ marginRight: 6 }}
                      onClick={() => handleDuplicate(f)} title="Duplicate filter">
                      <Copy size={12} />
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(f)} title="Delete filter">
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NewFilterModal open={newOpen} onClose={() => setNewOpen(false)}
        onCreate={handleCreate} saving={creating} />
      {dialog}
    </div>
  );
};
